/**
 * Resolves where to send an already-authenticated user who lands on the
 * login page with a `redirect` query param.
 *
 * The value is always resolved against `selfOrigin` via `new URL()` before
 * any decision is made — never classified by string prefix (e.g.
 * `startsWith('/')`). A prefix check is not safe here: the WHATWG URL
 * parser normalizes backslashes to slashes and strips control characters,
 * so strings like `/\evil.com` look same-site by prefix but resolve to a
 * different origin. Resolving first and comparing `.origin` closes that
 * gap. The result is allowed only if its origin is `selfOrigin` (returned
 * as a same-site path) or `allowedOrigin` (e.g. the regulation satellite
 * app for the current environment, returned as the full absolute URL).
 */
export function resolveLoginRedirectTarget(
  rawRedirect: unknown,
  selfOrigin: string,
  allowedOrigin: string
): string | null {
  if (typeof rawRedirect !== 'string' || !rawRedirect) return null;

  try {
    const resolved = new URL(rawRedirect, selfOrigin);

    if (resolved.origin === new URL(selfOrigin).origin) {
      return resolved.pathname + resolved.search + resolved.hash;
    }

    if (resolved.origin === new URL(allowedOrigin).origin) {
      return resolved.href;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Where the SAML complete page sends the user, or null to keep its default
 * landing page. Uses the same allowlist as the login page. Same-site values
 * are router paths, so they are placed behind the `#` of hash mode; an
 * allowed absolute URL (the regulation satellite app) is returned as-is.
 */
export function resolveSamlCompleteHref(
  rawRedirect: unknown,
  selfOrigin: string,
  allowedOrigin: string
): string | null {
  const target = resolveLoginRedirectTarget(
    rawRedirect,
    selfOrigin,
    allowedOrigin
  );
  if (!target) return null;
  return target.startsWith('/') ? `/#${target}` : target;
}
